import { TemplateResult } from 'lit';
import { RefOrCallback } from 'lit/directives/ref.js';
import { createViewBuilder, generateBindings } from './builder.js';
import { BuilderFactory, cssPropMetadataKey } from './factory.js';
import { Clazz, View } from './view.js';

export function asView<T, U>(
  options: TemplateViewOptionsWithFactory<T, U>
): [U & { View: Clazz<T & View> }, Clazz<T & View>];

export function asView<T>(
  options: TemplateViewOptions<T>
): [BuilderFactory<T> & { View: Clazz<T & View> }, Clazz<T & View>];

export function asView<T, U = BuilderFactory<T>>(
  options: TemplateViewOptions<T> | TemplateViewOptionsWithFactory<T, U>
) {
  let mapBuilder: ((_: BuilderFactory<T>) => U) | undefined;

  if (hasMapBuilder<T, U>(options)) {
    mapBuilder = options.mapBuilder;
  }

  class ComponentView extends View {
    constructor() {
      super();
      const mixin = new options.Props();
      Object.keys(mixin).forEach(key => {
        this[key as keyof this] = mixin[key as keyof T] as any;
      });
    }

    get body(): TemplateResult<any> {
      const eventBindings = generateBindings(this.events);
      const bindings = {
        ...eventBindings,
        ...this.attrs,
        '.view': this,
        '.animations': this.animations,
      };
      return options.template((this as unknown) as T, {
        bindings,
        styles: this.styles,
        elementRef: this.elementRef ?? (() => {}),
      });
    }
  }

  const _cssProps = options.cssProps ?? new Map<string, string | string[]>();
  if ((Reflect as any).getMetadata) {
    const mixin = new options.Props();
    Object.keys(mixin).forEach(key => {
      const propName: string | string[] = (Reflect as any).getMetadata(
        cssPropMetadataKey,
        mixin,
        key
      );
      if (propName) {
        _cssProps.set(key, propName);
      }
    });
  }

  let ViewFactory: any = function Factory() {
    return createViewBuilder(ComponentView, {}, _cssProps) as any;
  };
  if (mapBuilder) {
    ViewFactory = mapBuilder(ViewFactory);
  }

  ViewFactory.View = ComponentView;

  return [ViewFactory, ComponentView] as any;
}

export interface TemplateViewOptions<T> {
  Props: Clazz<T>;
  template: (
    props: T,
    options: {
      bindings: { [k: string]: any };
      styles: { [key: string]: string };
      elementRef: RefOrCallback;
    }
  ) => TemplateResult<any>;
  cssProps?: Map<string, string>;
}

export interface TemplateViewOptionsWithFactory<T, U>
  extends TemplateViewOptions<T> {
  mapBuilder: (_: BuilderFactory<T>) => U;
}

function hasMapBuilder<T, U>(
  options: any
): options is TemplateViewOptionsWithFactory<T, U> {
  return 'mapBuilder' in options;
}
